import React, { useState, useEffect, useRef } from 'react';
import { Box, Text, Flex, Image } from '@chakra-ui/react';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '../services/supabaseClient';

const MotionBox = motion(Box);

const IdleScreen = ({ onInteraction }) => {
  const [sponsors, setSponsors] = useState([]);
  const [loadingSponsors, setLoadingSponsors] = useState(true);
  const [currentSponsorIndex, setCurrentSponsorIndex] = useState(0);
  const videoRef = useRef(null);
  const [currentTime, setCurrentTime] = useState(new Date());

  // Carregar patrocinadores
  useEffect(() => {
    // Função para buscar patrocinadores
    async function fetchSponsors() {
      setLoadingSponsors(true);
      console.log('[IdleScreen] Buscando mídias do Supabase...');
      
      try {
        // Busca arquivos do bucket 'videos'
        const { data, error } = await supabase.storage.from('videos').list('', { limit: 100 });
        
        if (error) {
          console.error('[IdleScreen] Erro ao buscar mídias:', error);
          setSponsors([]);
          setLoadingSponsors(false);
          return;
        }
        
        if (!data || data.length === 0) {
          console.warn('[IdleScreen] Nenhuma mídia encontrada no bucket');
          setSponsors([]);
          setLoadingSponsors(false);
          return;
        }
        // Função auxiliar para obter URL pública ou assinada
        const getUrl = async (path) => {
          try {
            const { publicURL } = supabase.storage.from('videos').getPublicUrl(path);
            if (!publicURL) {
              // Tenta gerar signedUrl se não for público
              const { data } = await supabase.storage.from('videos').createSignedUrl(path, 60 * 60); // 1h
              return data?.signedUrl;
            }
            return publicURL;
          } catch (e) {
            console.error(`[IdleScreen] Erro ao obter URL para ${path}:`, e);
            return null;
          }
        };
        
        // Filtrar arquivos válidos
        const validFiles = data.filter(f => f.metadata && !f.name.startsWith('.'));
        console.log(`[IdleScreen] Total de arquivos válidos encontrados: ${validFiles.length}`);
        
        if (validFiles.length === 0) {
          console.warn('[IdleScreen] Nenhum arquivo válido encontrado');
          setSponsors([]);
          setLoadingSponsors(false);
          return;
        }
        
        // Processar cada arquivo
        const patrocinadores = [];
        for (const file of validFiles) {
          try {
            const ext = file.name.split('.').pop().toLowerCase();
            const isVideo = ['mp4', 'webm', 'mov'].includes(ext);
            const url = await getUrl(file.name);
            
            if (url) {
              patrocinadores.push({
                name: file.name,
                type: isVideo ? 'video' : 'image',
                url,
                timestamp: Date.now()
              });
            }
          } catch (e) {
            console.error(`[IdleScreen] Erro ao processar arquivo ${file.name}:`, e);
          }
        }
        
        // Atualizar estado com as mídias carregadas
        if (patrocinadores.length > 0) {
          console.log(`[IdleScreen] Mídias carregadas: ${patrocinadores.length} (${patrocinadores.filter(p => p.type === 'video').length} vídeos, ${patrocinadores.filter(p => p.type === 'image').length} imagens)`);
          setSponsors(patrocinadores);
          setCurrentSponsorIndex(0);
        } else {
          console.warn('[IdleScreen] Nenhuma mídia válida carregada');
          setSponsors([]);
        }
      } catch (error) {
        console.error('[IdleScreen] Erro ao carregar mídias:', error);
        setSponsors([]);
      } finally {
        setLoadingSponsors(false);
      }
    }
    
    // Carregar mídias inicialmente
    fetchSponsors();
    
    // Configurar recarga periódica a cada 30 minutos
    const reloadInterval = setInterval(() => {
      console.log('[IdleScreen] Recarregando mídias periodicamente...');
      fetchSponsors();
    }, 30 * 60 * 1000);
    
    return () => clearInterval(reloadInterval);
  }, []);

  // Atualizar hora atual
  useEffect(() => {
    const interval = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  // Log de mídia atual
  useEffect(() => {
    if (sponsors.length > 0 && sponsors[currentSponsorIndex]) {
      console.log('[IdleScreen] Exibindo mídia:', {
        nome: sponsors[currentSponsorIndex].name,
        tipo: sponsors[currentSponsorIndex].type,
        posicao: `${currentSponsorIndex + 1}/${sponsors.length}`
      });
    }
  }, [currentSponsorIndex, sponsors]);

  // Sistema de troca automática de mídias
  useEffect(() => {
    // Se não houver mídias, não faz nada
    if (sponsors.length === 0) return;
    
    const atual = sponsors[currentSponsorIndex];
    if (!atual) {
      console.error('[IdleScreen] Índice inválido, resetando para 0');
      setCurrentSponsorIndex(0);
      return;
    }
    
    let timer;
    
    // Configuração baseada no tipo de mídia
    if (atual.type === 'video') {
      // Para vídeos, configurar um timeout de segurança (60 segundos)
      console.log(`[IdleScreen] Iniciando vídeo: ${atual.name}`);
      timer = setTimeout(() => {
        console.warn(`[IdleScreen] Timeout de segurança acionado para vídeo: ${atual.name}`);
        handleNextMedia();
      }, 60000);
    } else {
      // Para imagens, avançar automaticamente após 10 segundos
      timer = setTimeout(() => {
        handleNextMedia();
      }, 10000);
    }
    
    // Limpar o timer quando o componente desmontar ou quando mudar de mídia
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [sponsors, currentSponsorIndex]);

  // Função para lidar com erro de vídeo
  const handleVideoError = (e) => {
    console.error('[IdleScreen] Erro ao reproduzir vídeo:', e);
    handleNextMedia();
  };

  // Função para avançar para a próxima mídia
  const handleNextMedia = () => {
    if (sponsors.length === 0) return;
    
    // Avançar para o próximo item
    const nextIndex = (currentSponsorIndex + 1) % sponsors.length;
    console.log(`[IdleScreen] Avançando para próxima mídia: ${nextIndex + 1}/${sponsors.length}`);
    setCurrentSponsorIndex(nextIndex);
  };

  // Formatar hora atual
  const formattedTime = currentTime.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit'
  });
  
  // Formatar data atual
  const formattedDate = currentTime.toLocaleDateString('pt-BR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });

  return (
    <MotionBox
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      height="100vh"
      width="100%"
      bg="black"
      position="relative"
      onClick={onInteraction}
      cursor="pointer"
      overflow="hidden"
    >
      {/* Publicidade em tela cheia */}
      <AnimatePresence mode="wait">
        {sponsors.length > 0 && (
          <MotionBox
            key={currentSponsorIndex}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1 }}
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            width="100%"
            height="100%"
            zIndex={1}
          >
            {/* Imagem do patrocinador em tela cheia */}
            <Box 
              width="100%" 
              height="100%"
              position="relative"
              overflow="hidden"
            >
              <Box
                position="absolute"
                top={0}
                left={0}
                right={0}
                bottom={0}
                bgGradient="linear(to-b, rgba(0,0,0,0.8), transparent 30%)"
                zIndex={3}
              />
              <Box
                position="absolute"
                bottom={0}
                left={0}
                right={0}
                height="150px"
                bgGradient="linear(to-t, rgba(0,0,0,0.8), transparent)"
                zIndex={3}
              />
              {/* Renderização condicional de vídeo ou imagem */}
              {loadingSponsors ? (
                <Box position="absolute" top="50%" left="50%" transform="translate(-50%, -50%)" zIndex={3}>
                  <Text color="white" fontSize="2xl">Carregando mídias...</Text>
                </Box>
              ) : sponsors.length === 0 ? (
                <Box position="absolute" top="50%" left="50%" transform="translate(-50%, -50%)" zIndex={3}>
                  <Text color="white" fontSize="2xl">Nenhuma mídia encontrada ou todas apresentaram erro</Text>
                </Box>
              ) : sponsors[currentSponsorIndex]?.type === 'video' ? (
                <Box
                  as="video"
                  ref={videoRef}
                  position="absolute"
                  top={0}
                  left={0}
                  right={0}
                  bottom={0}
                  width="100%"
                  height="100%"
                  objectFit="cover"
                  autoPlay
                  muted
                  playsInline
                  key={sponsors[currentSponsorIndex]?.url} // Forçar recriação do elemento
                  onError={handleVideoError}
                  onEnded={handleNextMedia}
                  onStalled={() => {
                    console.error('[IdleScreen] Vídeo travou (stalled)!');
                    handleVideoError();
                  }}
                  zIndex={2}
                  src={sponsors[currentSponsorIndex]?.url}
                />
              ) : (
                <Box
                  position="absolute"
                  top={0}
                  left={0}
                  right={0}
                  bottom={0}
                  zIndex={2}
                >
                  {/* Imagem da publicidade */}
                  <Image
                    src={sponsors[currentSponsorIndex]?.url || 'https://via.placeholder.com/1080x1920'}
                    alt={sponsors[currentSponsorIndex]?.name || 'Publicidade'}
                    width="100%"
                    height="100%"
                    objectFit="cover"
                  />
                </Box>
              )}
              
              {/* Nome do patrocinador removido conforme solicitado */}
            </Box>
          </MotionBox>
        )}
      </AnimatePresence>
      
      {/* Efeito degradê na parte superior */}
      <Box 
        position="absolute" 
        top={0} 
        left={0} 
        right={0} 
        height="180px"
        bgGradient="linear(to-b, rgba(0,0,0,0.85), rgba(0,0,0,0.6) 50%, rgba(0,0,0,0) 100%)"
        zIndex={10}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        px={6}
        pt={4}
      >
        {/* Logo da Barbearia */}
        <Image
          src={require('../assets/Logo-Barber.png')}
          alt="Barbearia Freire"
          width="120px"
          height="120px"
          objectFit="contain"
        />
        
        {/* Relógio e Data */}
        <Box textAlign="right">
          <Text fontSize="4xl" fontWeight="bold" color="white">{formattedTime}</Text>
          <Text fontSize="lg" color="whiteAlpha.800" textTransform="capitalize">{formattedDate}</Text>
        </Box>
      </Box>
      
      {/* Botão de Toque para Iniciar */}
      <MotionBox
        animate={{ 
          opacity: [0.5, 1, 0.5],
          scale: [1, 1.05, 1]
        }}
        transition={{ 
          repeat: Infinity,
          duration: 2
        }}
        position="absolute"
        bottom={10}
        left={0}
        right={0}
        zIndex={10}
        textAlign="center"
      >
        <Text 
          fontSize="2xl" 
          fontWeight="bold"
          color="white"
          textShadow="0 0 10px rgba(0,0,0,0.8)"
        >
          Toque para iniciar
        </Text>
      </MotionBox>
    </MotionBox>
  );
};

export default IdleScreen;
